import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { adminApi } from "../../services/api"

function formatNaira (v) {
  const n = Number(v || 0)
  try {
    return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(n)
  } catch {
    return `₦${n}`
  }
}

function safeLower (v) {
  return String(v || "").toLowerCase()
}

function toArray (value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.docs)) return value.docs
  if (Array.isArray(value?.documentations)) return value.documentations
  if (Array.isArray(value?.rows)) return value.rows
  if (Array.isArray(value?.results)) return value.results
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.topics)) return value.topics
  return []
}

function groupCount (items, keyFn) {
  return (items || []).reduce((acc, it) => {
    const k = keyFn(it) || "—"
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {})
}

function StatCard ({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-soft">
      <div className="text-xs font-semibold uppercase tracking-wide text-base-content/60">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      {hint ? <div className="mt-1 text-sm text-base-content/60">{hint}</div> : null}
    </div>
  )
}

function computeDocTypeRevenue (rows) {
  const seen = new Set()
  let total = 0
  for (const doc of rows || []) {
    for (const p of doc.payments || []) {
      if (safeLower(p?.paymentStatus) !== 'successful') continue
      const id = String(p._id || p.id || '')
      if (id && seen.has(id)) continue
      if (id) seen.add(id)
      total += Number(p.amount || 0)
    }
  }
  return total
}

function DocTypeSummary ({ title, rows }) {
  const count = (rows || []).length
  const revenue = computeDocTypeRevenue(rows)
  return (
    <div className="rounded-xl border border-base-300 p-4">
      <p className="font-semibold">{title}</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Generations</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{count}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Revenue generated</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{formatNaira(revenue)}</p>
        </div>
      </div>
    </div>
  )
}

function Section ({ title, subtitle, children, defaultOpen = true }) {
  return (
    <details className="group rounded-2xl border border-base-300 bg-base-100 shadow-soft" open={defaultOpen}>
      <summary className="cursor-pointer list-none px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-base-content/60">{subtitle}</p> : null}
          </div>
          <span className="mt-1 text-base-content/50 group-open:rotate-180 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </span>
        </div>
      </summary>
      <div className="px-5 pb-5 pt-0">{children}</div>
    </details>
  )
}

export default function AdminReports () {
  const navigate = useNavigate()
  const navItems = [
    { key: "schools", label: "Schools", icon: "M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z" },
    { key: "courses", label: "Courses", icon: "M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.905 59.905 0 0 1 12 3.493a59.902 59.902 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" },
    { key: "ideas", label: "Ideas", icon: "M9 18h6m-7.5 0h.01M7.5 6a3 3 0 0 1 6 0v2a2 2 0 0 0 .586 1.414L16 12.414V15a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.586l1.414-1A2 2 0 0 0 10 8V6a3 3 0 0 1-2.5 0Z" },
    { key: "documentation", label: "Documentation", icon: "M7.5 3.75h9A2.25 2.25 0 0 1 18.75 6v12A2.25 2.25 0 0 1 16.5 20.25h-9A2.25 2.25 0 0 1 5.25 18V6A2.25 2.25 0 0 1 7.5 3.75Zm3 4.5h6M10.5 12h6M10.5 15.75h6" },
    { key: "payments", label: "Payments", icon: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15.5" },
    { key: "referrals", label: "Referrals", icon: "M11.25 9.75h1.5m-1.5 3h1.5m-7.5 6.75h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75h-12A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 19.5Z" },
    { key: "students", label: "Students", icon: "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-3.382c0-1.023-.693-1.918-1.688-2.114A48.384 48.384 0 0 0 12 13.128v-3.382m0 0c1.02 0 2.002.115 2.688.263m-5.376 0a48.44 48.44 0 0 1 5.376 0" },
  ]
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])
  const [students, setStudents] = useState([])
  const [payments, setPayments] = useState([])
  const [referrals, setReferrals] = useState([])
  const [ideas, setIdeas] = useState([])
  const [documentations, setDocumentations] = useState([])
  const [docFilters, setDocFilters] = useState({
    schoolId: '',
    courseId: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  })

  const load = async () => {
    setLoading(true)
    setError("")
    try {
      const docParams = {
        limit: 500,
        page: 1,
        ...(docFilters.schoolId ? { schoolId: docFilters.schoolId } : {}),
        ...(docFilters.courseId ? { courseId: docFilters.courseId } : {}),
        ...(docFilters.dateFrom ? { dateFrom: docFilters.dateFrom } : {}),
        ...(docFilters.dateTo ? { dateTo: docFilters.dateTo } : {}),
        ...(docFilters.search ? { search: docFilters.search } : {})
      }
      const [
        schoolsRes,
        coursesRes,
        studentsRes,
        paymentsRes,
        referralsRes,
        ideasRes,
        docRes
      ] = await Promise.all([
        adminApi.schools.list(),
        adminApi.courses.list({ limit: 500 }),
        adminApi.students.list({ limit: 500 }),
        adminApi.payments.list({ limit: 500 }),
        adminApi.referrals.list({ limit: 500 }),
        adminApi.ideas.list({ limit: 200, page: 1 }),
        adminApi.documentations.list(docParams)
      ])

      setSchools(toArray(schoolsRes?.data?.data))
      setCourses(toArray(coursesRes?.data?.data))
      setStudents(toArray(studentsRes?.data?.data))
      setPayments(toArray(paymentsRes?.data?.data))
      setReferrals(toArray(referralsRes?.data?.data))

      const ideasData = ideasRes?.data?.data
      setIdeas(toArray(ideasData))

      const docsData = docRes?.data?.data
      setDocumentations(toArray(docsData?.documentations || docsData))
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load reports")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFilters.schoolId, docFilters.courseId, docFilters.dateFrom, docFilters.dateTo, docFilters.search])

  const paymentStats = useMemo(() => {
    const byStatus = groupCount(payments, (p) => safeLower(p?.paymentStatus) || "unknown")
    const byType = groupCount(payments, (p) => p?.productType || "—")
    const successfulTotal = (payments || [])
      .filter((p) => safeLower(p?.paymentStatus) === "successful")
      .reduce((acc, p) => acc + Number(p?.amount || 0), 0)
    return { byStatus, byType, successfulTotal }
  }, [payments])

  const referralStats = useMemo(() => {
    const byStatus = groupCount(referrals, (r) => safeLower(r?.status) || "unknown")
    const totalAmount = (referrals || []).reduce((acc, r) => acc + Number(r?.amount ?? 2000), 0)
    return { byStatus, totalAmount }
  }, [referrals])

  const studentStats = useMemo(() => {
    const active = (students || []).filter((s) => Number(s?.status) === 1).length
    const inactive = (students || []).filter((s) => Number(s?.status) === 0).length
    const bySchool = groupCount(students, (s) => s?.school || "—")
    return { active, inactive, bySchool }
  }, [students])

  const ideasStats = useMemo(() => {
    const total = (ideas || []).length
    const approved = (ideas || []).filter((t) => !!t?.isApproved).length
    const pending = total - approved
    const byCategory = groupCount(ideas, (t) => t?.category || "—")
    const byDifficulty = groupCount(ideas, (t) => t?.difficulty || "—")
    return { total, approved, pending, byCategory, byDifficulty }
  }, [ideas])

  const documentationStats = useMemo(() => {
    const total = (documentations || []).length
    const byStatus = groupCount(documentations, (d) => d?.generationStatus || "—")
    const byType = {
      DOCUMENTATION: (documentations || []).filter((d) => d.generationType === 'DOCUMENTATION'),
      PROPOSAL: (documentations || []).filter((d) => d.generationType === 'PROPOSAL'),
      SIWES_REPORT: (documentations || []).filter((d) => d.generationType === 'SIWES_REPORT')
    }
    return { total, byStatus, byType }
  }, [documentations])

  const generationRevenueStats = useMemo(() => {
    const docRevenue = computeDocTypeRevenue(documentationStats.byType.DOCUMENTATION)
    const proposalRevenue = computeDocTypeRevenue(documentationStats.byType.PROPOSAL)
    const siwesRevenue = computeDocTypeRevenue(documentationStats.byType.SIWES_REPORT)
    const totalRevenue = docRevenue + proposalRevenue + siwesRevenue
    const totalGenerations =
      documentationStats.byType.DOCUMENTATION.length +
      documentationStats.byType.PROPOSAL.length +
      documentationStats.byType.SIWES_REPORT.length
    return { docRevenue, proposalRevenue, siwesRevenue, totalRevenue, totalGenerations }
  }, [documentationStats])

  return (
    <div className="min-h-screen bg-base-200 text-base-content">
      <nav className="fixed top-0 z-50 w-full bg-base-100 border-b border-base-300">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="self-center text-xl font-semibold sm:text-2xl whitespace-nowrap">AStudent</span>
              <span className="badge badge-sm">Admin</span>
              <span className="badge badge-outline badge-sm">Report</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-sm btn-ghost" type="button" onClick={() => navigate("/admin/dashboard")}>
                Back to dashboard
              </button>
              <button className="btn btn-sm btn-ghost" type="button" onClick={load} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </nav>

      <aside className="fixed top-0 left-0 z-40 w-64 h-screen pt-20 transition-transform -translate-x-full bg-base-100 border-r border-base-300 sm:translate-x-0" aria-label="Sidebar">
        <div className="h-full px-3 pb-4 overflow-y-auto bg-base-100">
          <ul className="space-y-4 font-medium pt-4">
            <li className="py-1 rounded-xl">
              <button type="button" onClick={() => navigate("/admin/dashboard")} className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75h6v6h-6v-6Zm10.5 0h6v10.5h-6V3.75ZM3.75 14.25h6v6h-6v-6Zm10.5 4.5h6v1.5h-6v-1.5Z" />
                </svg>
                <span className="ms-3 whitespace-nowrap">Dashboard</span>
              </button>
            </li>
            <li className="py-1 rounded-xl bg-primary/10 text-primary">
              <button type="button" className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5h15M6.75 17.25V9.75M12 17.25V6.75M17.25 17.25v-12" />
                </svg>
                <span className="ms-3 whitespace-nowrap">Report</span>
              </button>
            </li>
            {navItems.map(({ key, label, icon }) => (
              <li key={key} className="py-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => navigate(key === "schools" ? "/admin/schools" : `/admin/dashboard?tab=${key}`)}
                  className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                  </svg>
                  <span className="ms-3 whitespace-nowrap">{label}</span>
                </button>
              </li>
            ))}
            <li className="pt-4 mt-4 border-t border-base-300">
              <button type="button" onClick={() => navigate("/admin", { replace: true })} className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
                </svg>
                <span className="flex-1 ms-3 whitespace-nowrap text-left">Logout</span>
              </button>
            </li>
          </ul>
        </div>
      </aside>

      <div className="p-4 sm:ml-64">
        <div className="p-5 border border-base-300 rounded-2xl mt-14 bg-base-100 min-h-[calc(100vh-8rem)] shadow-soft">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Admin Report</h1>
              <p className="mt-1 text-sm text-base-content/60">
                KPIs and breakdowns across ideas, document generation, payments, students, courses, schools, and referrals.
              </p>
            </div>
          </div>

          {error ? <p className="text-error mt-3">{error}</p> : null}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary"></span>
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard label="Total revenue" value={formatNaira(generationRevenueStats.totalRevenue)} hint={`${generationRevenueStats.totalGenerations} total generations`} />
                <StatCard label="Students" value={students.length} hint={`${studentStats.active} active · ${studentStats.inactive} inactive`} />
                <StatCard label="Ideas" value={ideasStats.total} hint={`${ideasStats.approved} approved · ${ideasStats.pending} pending`} />
                <StatCard label="Documentation" value={documentationStats.byType.DOCUMENTATION.length} hint={formatNaira(generationRevenueStats.docRevenue)} />
                <StatCard label="Proposal" value={documentationStats.byType.PROPOSAL.length} hint={formatNaira(generationRevenueStats.proposalRevenue)} />
                <StatCard label="SIWES Report" value={documentationStats.byType.SIWES_REPORT.length} hint={formatNaira(generationRevenueStats.siwesRevenue)} />
              </div>

              <div className="rounded-2xl border border-base-300 p-4 bg-base-100">
                <p className="font-semibold mb-3">Report filters</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <label className="form-control">
                    <span className="label-text text-xs">School</span>
                    <select className="select select-bordered select-sm" value={docFilters.schoolId} onChange={(e) => setDocFilters((f) => ({ ...f, schoolId: e.target.value }))}>
                      <option value="">All schools</option>
                      {schools.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </select>
                  </label>
                  <label className="form-control">
                    <span className="label-text text-xs">Course</span>
                    <select className="select select-bordered select-sm" value={docFilters.courseId} onChange={(e) => setDocFilters((f) => ({ ...f, courseId: e.target.value }))}>
                      <option value="">All courses</option>
                      {courses.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </label>
                  <label className="form-control">
                    <span className="label-text text-xs">From date</span>
                    <input type="date" className="input input-bordered input-sm" value={docFilters.dateFrom} onChange={(e) => setDocFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
                  </label>
                  <label className="form-control">
                    <span className="label-text text-xs">To date</span>
                    <input type="date" className="input input-bordered input-sm" value={docFilters.dateTo} onChange={(e) => setDocFilters((f) => ({ ...f, dateTo: e.target.value }))} />
                  </label>
                  <label className="form-control">
                    <span className="label-text text-xs">Search</span>
                    <input type="search" className="input input-bordered input-sm" placeholder="Student, project…" value={docFilters.search} onChange={(e) => setDocFilters((f) => ({ ...f, search: e.target.value }))} />
                  </label>
                </div>
              </div>

              <Section title="Ideas report" subtitle="Approvals, categories, and difficulty distribution.">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By category</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>Category</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(ideasStats.byCategory).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By difficulty</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>Difficulty</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(ideasStats.byDifficulty).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Project documentation report" subtitle="Generation count and revenue from documentation.">
                <DocTypeSummary title="Documentation" rows={documentationStats.byType.DOCUMENTATION} />
              </Section>

              <Section title="Proposal report" subtitle="Generation count and revenue from proposals.">
                <DocTypeSummary title="Proposal" rows={documentationStats.byType.PROPOSAL} />
              </Section>

              <Section title="SIWES report" subtitle="Generation count and revenue from SIWES reports.">
                <DocTypeSummary title="SIWES Report" rows={documentationStats.byType.SIWES_REPORT} />
              </Section>

              <Section title="Payments report" subtitle="Status, product types, and total successful amount.">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By status</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>Status</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(paymentStats.byStatus).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By product type</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>Type</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(paymentStats.byType).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Students report" subtitle="Active/inactive, and distribution by school text on profile.">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">Status</p>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <StatCard label="Active" value={studentStats.active} />
                      <StatCard label="Inactive" value={studentStats.inactive} />
                    </div>
                  </div>
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By school</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>School</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(studentStats.bySchool).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Courses report" subtitle="Total courses and per-school mapping.">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <StatCard label="Total courses" value={courses.length} />
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">Courses by school</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>School</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(groupCount(courses, (c) => c?.school?.name || c?.school || "—")).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Schools report" subtitle="Total schools configured.">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard label="Total schools" value={schools.length} />
                </div>
              </Section>

              <Section title="Referrals report" subtitle="Counts by status and total commission amount.">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-base-300 p-4">
                    <p className="font-semibold">By status</p>
                    <div className="mt-2 overflow-x-auto">
                      <table className="table table-sm">
                        <thead><tr><th>Status</th><th>Count</th></tr></thead>
                        <tbody>
                          {Object.entries(referralStats.byStatus).map(([k, v]) => (
                            <tr key={k}><td>{k}</td><td className="tabular-nums">{v}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <StatCard label="Total referral amount" value={formatNaira(referralStats.totalAmount)} hint={`${referrals.length} records`} />
                </div>
              </Section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

